import React, { Fragment } from 'react';
import { COLORS } from 'Components/utils/color/colors';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler';
import { EventAnimationControls } from './EventAnimationControls';
import type { AnimationEvent } from './EventAnimationSlide';
import { EventAnimationSlide } from './EventAnimationSlide';
import { EventAnimationTitles } from './EventAnimationTitles';
type Props = {
  events: Array<AnimationEvent>;
};
export type { AnimationEvent };
export const EventAnimation = ({ events }: Props) => {
  const track = useTracking();
  const hasMultipleOffers = events.length > 1;
  const offerSlides = events.map((event) => (
    <EventAnimationSlide
      className="min-h-full"
      event={event}
      key={event.content.title}
      onClick={(e) =>
        track({
          actionGA: 'eventAnimation',
          labelGA: e.content.title,
        })
      }
    />
  ));
  return (
    <SliderHandler
      hasKeyboardSupport
      hasLazyload
      hasSwipeSupport
      items={offerSlides}
      itemsAreaClassName="gap-10 pe-10 -me-10 md:pe-0 md:me-0 md:gap-0"
    >
      {({
        hasControls,
        isNextControlDisabled,
        isPreviousControlDisabled,
        itemsNode,
        index,
        onNext,
        onPrevious,
        onIndex,
      }) => (
        <Fragment>
          {hasMultipleOffers && (
            <EventAnimationTitles events={events} index={index} onIndex={onIndex} />
          )}
          <div className="relative isolate overflow-hidden md:rounded-12 md:shadow-lg">
            {itemsNode}
            {hasControls && (
              <div
                className={classnames(
                  'hidden w-full items-center justify-between px-50 md:flex',
                  'pointer-events-none absolute top-1/2 -translate-y-1/2',
                )}
              >
                <ArrowButton
                  className={classnames('pointer-events-auto -ms-20 h-40 flex-none shadow-sm', {
                    invisible: isPreviousControlDisabled,
                  })}
                  color={COLORS.BLACK}
                  isDisabled={isPreviousControlDisabled}
                  label={t('common.previous')}
                  onClick={onPrevious}
                />
                <ArrowButton
                  className={classnames('pointer-events-auto -me-20 h-40 flex-none shadow-sm', {
                    invisible: isNextControlDisabled,
                  })}
                  color={COLORS.BLACK}
                  isDisabled={isNextControlDisabled}
                  label={t('common.next')}
                  onClick={onNext}
                  rotation={180}
                />
              </div>
            )}
          </div>
          {hasMultipleOffers && (
            <EventAnimationControls
              className="mt-20"
              index={index}
              isNextControlDisabled={isNextControlDisabled}
              isPreviousControlDisabled={isPreviousControlDisabled}
              onNext={onNext}
              onPrevious={onPrevious}
              total={events.length}
            />
          )}
        </Fragment>
      )}
    </SliderHandler>
  );
};
